import React, { useRef, useState } from 'react';
import { Button, SparkIcon, ReadOnlyGuard } from '@spark';
import {
    acceptAttributeForTaskType,
    supportedFormatsLabelForTaskType,
} from '@/features/datasets/utils/datasetUploadFlow';
import {
    partitionFilesBySupport,
    showUnsupportedFileTypeToast,
    unsupportedFileTypeCardMessage,
} from '@/features/datasets/utils/unsupportedFileHandling';

const CARD_COPY = {
    dataset: {
        title: 'Upload Dataset',
        buttonLabel: 'Upload dataset file',
        illustration: '/images/upload-dataset-illustration.png',
    },
    benchmark: {
        title: 'Upload Benchmark',
        buttonLabel: 'Upload benchmark file',
        illustration: '/images/upload-benchmark-illustration.png',
    },
};

/**
 * Dashed empty upload card for Data Overview (Figma 3215:11841).
 * Upload opens a file picker and hands selected/dropped files to the parent;
 * Library opens From Library scoped to this side. Multi-file drag/select is
 * supported — the parent seeds the Upload Dataset modal with every file.
 */
const DataOverviewUploadEmptyCard = ({
    target = 'dataset',
    taskType,
    /** Agentic task (autonomy level ≠ 1): json/jsonl/parquet only. */
    isAgentic = false,
    onUploadFiles,
    onOpenLibrary,
    disabled = false,
}) => {
    const inputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [formatError, setFormatError] = useState(null);
    const copy = CARD_COPY[target] || CARD_COPY.dataset;
    const formatOptions = { isAgentic };
    const acceptAttribute = acceptAttributeForTaskType(taskType, formatOptions);
    const formatsLabel = supportedFormatsLabelForTaskType(taskType, formatOptions);

    const handleFiles = (fileList) => {
        const files = Array.from(fileList || []).filter(Boolean);
        if (!files.length || disabled) return;
        const { supported, unsupported } = partitionFilesBySupport(files, taskType, formatOptions);
        setFormatError(
            unsupported.length ? unsupportedFileTypeCardMessage(taskType, formatOptions) : null
        );
        if (unsupported.length) showUnsupportedFileTypeToast(unsupported, taskType, formatOptions);
        if (supported.length) onUploadFiles?.(supported);
    };

    const className = [
        'data-overview-upload-empty',
        isDragOver ? 'data-overview-upload-empty--drag' : '',
        formatError ? 'data-overview-upload-empty--error' : '',
        disabled ? 'data-overview-upload-empty--disabled' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            className={className}
            onDragOver={(e) => {
                if (disabled) return;
                e.preventDefault();
                setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
                if (disabled) return;
                e.preventDefault();
                setIsDragOver(false);
                handleFiles(e.dataTransfer?.files);
            }}
        >
            <img
                className="data-overview-upload-empty__illustration"
                src={copy.illustration}
                alt=""
                aria-hidden="true"
            />
            <div className="data-overview-upload-empty__text">
                <p className="data-overview-upload-empty__title">{copy.title}</p>
                <p className="data-overview-upload-empty__subtitle">{formatsLabel}</p>
            </div>
            <div className="data-overview-upload-empty__actions">
                <Button
                    size="medium"
                    variant="tertiary"
                    startIcon={<SparkIcon slug="upload" size="medium" />}
                    showStartIcon
                    disabled={disabled}
                    disableWhenReadOnly
                    onClick={() => inputRef.current?.click()}
                >
                    {copy.buttonLabel}
                </Button>
                <ReadOnlyGuard>
                    <button
                        type="button"
                        className="data-overview-upload-empty__library-link"
                        disabled={disabled}
                        onClick={() => {
                            if (!disabled) onOpenLibrary?.();
                        }}
                    >
                        <SparkIcon slug="library" size="small" />
                        <span>Choose From your Uploads</span>
                    </button>
                </ReadOnlyGuard>
            </div>
            {formatError && (
                <div className="data-overview-upload-empty__error" role="alert">
                    <span>{formatError}</span>
                    <button
                        type="button"
                        className="data-overview-upload-error-dismiss"
                        aria-label="Dismiss error"
                        onClick={() => setFormatError(null)}
                    >
                        <SparkIcon slug="close" size="small" />
                    </button>
                </div>
            )}
            <input
                ref={inputRef}
                type="file"
                accept={acceptAttribute}
                multiple
                style={{ display: 'none' }}
                disabled={disabled}
                onChange={(e) => {
                    handleFiles(e.target.files);
                    e.target.value = '';
                }}
            />
        </div>
    );
};

export default DataOverviewUploadEmptyCard;
